'use client';

import { useBookProStore } from '@/store/useLocaleBookProStore';
import {
  checkRestoreStatus,
  clearLocalSession,
  getLocaleFromSessionId,
  migrateLegacySession
} from '@/utils/sessionRestoration';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import Link from "next/link";
import { useRouter, useSearchParams } from 'next/navigation';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import Header from './components/Header';
import NavigationButtons from './components/NavigationButtons';
import RestoringLoader from './components/RestoringLoader';
import ResetModal from "./components/Screen4/ResetModal";
import SessionRestoreDialog from './components/SessionRestoreDialog';

// Lazy load all screens for better performance
const GenreSelection = lazy(() => import('./screens/GenreSelection'));
const AuthorProfile = lazy(() => import('./screens/AuthorProfile'));
const AuthorQuestionnaire = lazy(() => import('./screens/AuthorQuestionnaire'));
const TitleSelection = lazy(() => import('./screens/TitleSelection'));
const EmailCapture = lazy(() => import('./screens/EmailCapture'));
const CoverTypeSelection = lazy(() => import('./screens/CoverTypeSelection'));
const AuthorImageUpload = lazy(() => import('./screens/AuthorImageUpload'));
const CoverDesigner = lazy(() => import('./screens/CoverDesigner'));
const TableOfContents = lazy(() => import('./screens/TableOfContents'));
const SongSelection = lazy(() => import('./screens/SongSelection'));
const ProductOptions = lazy(() => import('./screens/ProductOptions'));
const CheckoutSummary = lazy(() => import('./screens/CheckoutSummary'));
// import Background from './components/Background'; // Removed for performance
import testimonialSliderJson from '@/data/createBook/testimonials.json';
import * as Sentry from '@sentry/nextjs';
import { trackCustomizeProduct } from '@/utils/pixelTracking';
import ActionWidget from './components/ActionWidget';
import BookTailorInfoSmall from './components/Screen9/BookTailorInfoSmall';
import TestimonialSlider from './components/Screen9/TestimonialSlider';
import ScreenSkeleton from './components/ScreenSkeleton';
import Stepper from './components/Stepper';

// Separate component that uses useSearchParams
function CreateBookProContent() {
  const locale = useLocale();
  const tReset = useTranslations('wizard.reset');
  const tStepper = useTranslations('wizard.stepper');
  const tCommon = useTranslations('common');
  const {
    currentStep,
    initializeSession,
    goToPreviousStep,
    hasSeenEmailScreen,
    restoreFromCloud,
    clearCurrentSession,
    resetForm,
    setUserIp,
    setUserAgent,
    sessionId,
    userIp,
    sentryUserId,
    setSentryUserId
  } = useBookProStore();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  // Session restoration state
  const [restoreStatus, setRestoreStatus] = useState('idle'); // idle, loading, showing_dialog, ready, error
  const [restoreDialogMode, setRestoreDialogMode] = useState(null); // equal, differ, cloud_failed
  const [cloudData, setCloudData] = useState(null);
  const [localData, setLocalData] = useState(null);
  const [restoreError, setRestoreError] = useState(null);

  // Helper function to remove sessionId from URL
  const removeSessionIdFromUrl = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('sessionId');
    router.replace(currentUrl.pathname + currentUrl.search, { scroll: false });
    console.log('🧹 Removed sessionId from URL');
  };

  // Ensure isHydrated starts as false on both server and client to avoid hydration mismatch
  const [isHydrated, setIsHydrated] = useState(false);

  // Reset Modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  // Updated to show modal instead of calling resetForm directly
  const handleResetClick = useCallback(() => {
    setShowResetModal(true);
  }, []);

  const handleCloseResetModal = useCallback(() => {
    setShowResetModal(false);
  }, []);


  const handleConfirmReset = useCallback(() => {
    resetForm();
    setIsResetModalOpen(false);
  }, []);

  const scrollTimeoutRef = useRef(null);

  // Handle session restoration on mount
  useEffect(() => {
    const handleSessionRestoration = async () => {
      // Migrate legacy session data if exists (one-time migration)
      migrateLegacySession(locale);

      // Check if there's a sessionId in URL
      const sessionIdFromUrl = searchParams.get('sessionId');

      if (sessionIdFromUrl) {
        // Check if session ID belongs to a different locale
        const sessionLocale = getLocaleFromSessionId(sessionIdFromUrl);
        if (sessionLocale && sessionLocale !== locale) {
          // Redirect to the correct locale for this session
          console.log(`🔄 Session belongs to ${sessionLocale}, redirecting from ${locale}`);
          router.replace(`/${sessionLocale}/create-your-book?sessionId=${sessionIdFromUrl}`);
          return;
        }

        // Start restoration flow
        setRestoreStatus('loading');

        try {
          // Check restore status with locale
          const result = await checkRestoreStatus(sessionIdFromUrl, locale);

          // Debug log for cloud session data
          if (result.cloudData) {
            console.log('📦 Cloud session data received:', {
              status: result.status,
              locale: locale,
              is_paid: result.cloudData?.is_paid,
              paid_at: result.cloudData?.paid_at,
              order_id: result.cloudData?.order_id
            });
          }

          switch (result.status) {
            case 'no_local':
              // No local session, restore cloud automatically
              restoreFromCloud(result.cloudData);
              initializeSession();
              setRestoreStatus('ready');
              // Small delay to ensure state has fully updated before hydrating
              setTimeout(() => {
                setIsHydrated(true);
                removeSessionIdFromUrl();
              }, 50);
              break;

            case 'sessions_equal':
              // Check if cloud session has is_paid - if so, initialize fresh session
              if (result.cloudData?.is_paid) {
                console.log('💳 Cloud session has is_paid=true, initializing fresh session');
                clearLocalSession(locale); // Clear localStorage for this locale
                clearCurrentSession(); // Reset in-memory store state to defaults
                initializeSession(); // Create new sessionId
                setRestoreStatus('ready');
                setIsHydrated(true);
                removeSessionIdFromUrl();
              } else {
                // Sessions are equal, auto-apply cloud session
                restoreFromCloud(result.cloudData);
                initializeSession();
                setRestoreStatus('ready');
                setTimeout(() => {
                  setIsHydrated(true);
                  removeSessionIdFromUrl();
                }, 50);
              }
              break;

            case 'sessions_differ':
              // Check if cloud session has is_paid - if so, initialize fresh session
              if (result.cloudData?.is_paid) {
                console.log('💳 Cloud session has is_paid=true, initializing fresh session');
                clearLocalSession(locale); // Clear localStorage for this locale
                clearCurrentSession(); // Reset in-memory store state to defaults
                initializeSession(); // Create new sessionId
                setRestoreStatus('ready');
                setIsHydrated(true);
                removeSessionIdFromUrl();
              } else {
                // Sessions differ, auto-apply cloud session
                restoreFromCloud(result.cloudData);
                clearLocalSession(locale); // Clear old local session for this locale
                initializeSession();
                setRestoreStatus('ready');
                setTimeout(() => {
                  setIsHydrated(true);
                  removeSessionIdFromUrl();
                }, 50);
              }
              break;

            case 'cloud_failed':
              // Cloud failed, show dialog with local option
              console.error('⚠️ Cloud restore failed, showing dialog with local fallback');
              console.error('Error:', result.error);
              setLocalData(result.localData);
              setRestoreDialogMode('cloud_failed');
              setRestoreStatus('showing_dialog');
              setRestoreError(result.error);
              break;

            case 'error':
              // Both failed
              console.error('❌ Both cloud and local restore failed');
              console.error('Error:', result.error);
              setRestoreError(result.error);
              setRestoreStatus('error');
              // Initialize with a new session
              initializeSession();
              setIsHydrated(true);
              break;
          }
        } catch (error) {
          console.error('Error during restoration:', error);
          setRestoreError(error.message);
          setRestoreStatus('error');
          initializeSession();
          setIsHydrated(true);
        }
      } else {
        // No sessionId in URL, normal initialization
        initializeSession();
        setIsHydrated(true);
      }
    };

    handleSessionRestoration();
  }, [searchParams, locale, initializeSession, restoreFromCloud, clearCurrentSession, router]);

  useEffect(() => {
    // Check if mobile on mount and window resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track CustomizeProduct event once page is hydrated
  useEffect(() => {
    if (isHydrated) {
      trackCustomizeProduct();
    }
  }, [isHydrated]);

  // Capture user IP and user agent on page load
  useEffect(() => {
    // Set user agent immediately (client-side)
    if (typeof navigator !== 'undefined') {
      setUserAgent(navigator.userAgent);
    }

    // Fetch IP from API
    fetch('/api/user-info')
      .then(res => res.json())
      .then(data => {
        if (data.ip) {
          setUserIp(data.ip);
        }
      })
      .catch(err => console.error('Failed to fetch user IP:', err));
  }, [setUserIp, setUserAgent]);

  // Bidirectional Sentry ↔ BookTailor session linking
  useEffect(() => {
    // Forward link: BookTailor session ID → Sentry (searchable tag)
    if (sessionId) {
      Sentry.setTag('book_session_id', sessionId);
    }

    // Merge IP into existing user context (preserves anon_UUID from SentryTracker)
    if (userIp) {
      const existingUser = Sentry.getCurrentScope().getUser();
      Sentry.setUser({
        ...existingUser,
        ip_address: userIp,
      });
    }

    // Reverse link: Sentry user ID → BookTailor store
    const sentryAnonId = localStorage.getItem('sentry_anon_user_id');
    if (sentryAnonId && sentryAnonId !== sentryUserId) {
      setSentryUserId(sentryAnonId);
    }

    Sentry.setTag('locale', locale);
  }, [userIp, sessionId, locale]);

  // Handlers for session restore dialog
  const handleUseCloud = async () => {
    if (cloudData) {
      const sessionIdFromUrl = searchParams.get('sessionId');
      // First restore from cloud data (this replaces the entire state)
      const success = restoreFromCloud(cloudData);

      if (success) {
        // Set the sessionId from URL
        initializeSession(sessionIdFromUrl);
        // Clear old local session after successful restore for this locale
        clearLocalSession(locale);
        setRestoreStatus('ready');

        // Small delay to ensure state has fully updated
        setTimeout(() => {
          setIsHydrated(true);
          removeSessionIdFromUrl();
        }, 50);
      } else {
        console.error('Failed to restore from cloud, using local session');
        handleUseLocal();
      }
    }
  };

  const handleUseLocal = () => {
    // Use local session (already loaded by Zustand persist)
    // Just mark as ready - local data is already in the store via Zustand persist
    setRestoreStatus('ready');
    setIsHydrated(true);
    removeSessionIdFromUrl();
  };

  const handleCloseDialog = () => {
    // If user closes dialog without choosing, default to local session
    handleUseLocal();
  };

  const handleDisabledButtonClick = useCallback(() => {
    if (currentStep === 2) {
      if (window.stepTwoHandleDisabledClick) {
        window.stepTwoHandleDisabledClick();
      }
    }
  }, [currentStep]);

  // Desktop: 6 steps
  const desktopStepsName = [
    tStepper("genre"),
    tStepper("author"),
    tStepper("details"),
    tStepper("title"),
    tStepper("cover"),
    tStepper("preview"),
  ];

  // Mobile: 5 steps
  const mobileStepsName = [
    tStepper("author"),
    tStepper("details"),
    tStepper("cover"),
    tStepper("preview"),
    tStepper("checkout")
  ];

  const stepsName = isMobile ? mobileStepsName : desktopStepsName;
  const totalSteps = 6;

  // Map current screen (1-12) to display step
  const getDisplayStep = (currentStep) => {
    if (isMobile) {
      // Mobile mapping with fractional steps
      const mobileStepMapping = {
        1: 1,    // Genre
        2: 2,    // Author
        3: 3,    // Details (Questions)
        4: 4,    // Title
        5: 4,    // Email/Consent → Cover
        6: 5,    // Cover Type Selection → Cover
        7: 5,    // Photo → Cover
        8: 5,    // Cover Design → Cover
        9: 6,    // Preview/TOC → Preview
        10: 6,   // Song Selection → Preview
        11: 6,   // Order → Checkout
        12: 6    // Payment → Checkout
      };
      return mobileStepMapping[currentStep] || 1;
    } else {
      // Desktop mapping (7 steps)
      const desktopStepMapping = {
        1: 1,    // Genre
        2: 2,    // Author
        3: 3,    // Details (Questions)
        4: 4,    // Title
        5: 4,    // Email/Consent → Cover
        6: 5,    // Cover Type Selection → Cover
        7: 5,    // Photo → Cover
        8: 5,    // Cover Design → Cover
        9: 6,    // Preview/TOC
        10: 6,   // Song Selection → Preview
        11: 7,   // Order → Checkout
        12: 7    // Payment → Checkout
      };
      return desktopStepMapping[currentStep] || 1;
    }
  };

  const renderScreen = () => {
    switch (currentStep) {
      case 1:
        return <GenreSelection />;
      case 2:
        return <AuthorProfile />;
      case 3:
        return <AuthorQuestionnaire />;
      case 4:
        return <TitleSelection />;
      case 5:
        return <EmailCapture />;
      case 6:
        return <CoverTypeSelection />;
      case 7:
        return <AuthorImageUpload />;
      case 8:
        return <CoverDesigner />;
      case 9:
        return <TableOfContents />;
      case 10:
        return <SongSelection />;
      case 11:
        return <ProductOptions />;
      case 12:
        return <CheckoutSummary />;
      default:
        return <GenreSelection />;
    }
  };

  // Show loading screen during restoration
  if (restoreStatus === 'loading') {
    return <RestoringLoader />;
  }





  return (
    <>
      {/* Session Restore Dialog */}
      <SessionRestoreDialog
        isOpen={restoreStatus === 'showing_dialog'}
        mode={restoreDialogMode}
        cloudData={cloudData}
        localData={localData}
        error={restoreError}
        onUseCloud={handleUseCloud}
        onUseLocal={handleUseLocal}
        onClose={handleCloseDialog}
      />

      <div className="min-h-screen bg-[#F9F6EB] flex flex-col">
        {isHydrated && currentStep === 1 && <Header />}

        {/* Render stepper immediately, even while hydrating */}
        {isHydrated && currentStep <= 10 && (
          <Stepper
            currentStep={getDisplayStep(currentStep)}
            totalSteps={totalSteps}
            stepsName={stepsName}
          />
        )}


        {isHydrated && (
          <>
            <ResetModal
              isOpen={showResetModal}
              onClose={handleCloseResetModal}
              onConfirm={handleConfirmReset}
            />
            <AnimatePresence>
              {currentStep !== 1 && (
                <motion.div
                  className={`flex max-w-[850px] w-full px-5 mx-auto ${currentStep === 11 ? "" : "-mt-4"}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <button onClick={goToPreviousStep} className={`pointer ${currentStep > 10 ? 'mt-8' : ''}`} aria-label="Go to previous step">
                    {isMobile ? (
                      <svg
                        width="30"
                        height="20"
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.536635 12.1992C-0.179063 12.9149 -0.179063 14.0752 0.536635 14.7909L12.1992 26.4535C12.9148 27.1692 14.0752 27.1692 14.7909 26.4535C15.5065 25.7378 15.5065 24.5775 14.7909 23.8618L4.42414 13.4951L14.7909 3.1283C15.5065 2.4127 15.5065 1.2523 14.7909 0.536699C14.0752 -0.179001 12.9148 -0.179001 12.1992 0.536699L0.536635 12.1992ZM32.0703 11.6625L1.83244 11.6625L1.83244 15.3277L32.0703 15.3277L32.0703 11.6625Z"
                          fill="#747474"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="26"
                        height="18"
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.536635 12.1992C-0.179063 12.9149 -0.179063 14.0752 0.536635 14.7909L12.1992 26.4535C12.9148 27.1692 14.0752 27.1692 14.7909 26.4535C15.5065 25.7378 15.5065 24.5775 14.7909 23.8618L4.42414 13.4951L14.7909 3.1283C15.5065 2.4127 15.5065 1.2523 14.7909 0.536699C14.0752 -0.179001 12.9148 -0.179001 12.1992 0.536699L0.536635 12.1992ZM32.0703 11.6625L1.83244 11.6625L1.83244 15.3277L32.0703 15.3277L32.0703 11.6625Z"
                          fill="#747474"
                        />
                      </svg>
                    )}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div className={`w-full  ${currentStep === 11 ? '' : 'pb-16 sm:pb-20 md:pb-28 lg:pb-36'}`}>
              <div className={` ${currentStep === 11 ? '' : currentStep === 8 ? 'mb-0 sm:mb-10 md:mb-12 lg:mb-[50px]' : 'mb-8 sm:mb-10 md:mb-12 lg:mb-[50px]'} `}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeInOut" }}
                    onAnimationComplete={() => {
                      const scrollElement =
                        document.scrollingElement ||
                        document.documentElement ||
                        document.body;
                      scrollElement.scrollTo({ top: (currentStep === 10 ? 50 : 0), behavior: "smooth" });
                    }}
                  >
                    <Suspense fallback={<ScreenSkeleton />}>
                      {renderScreen()}
                    </Suspense>
                  </motion.div>
                </AnimatePresence>
              </div>
              {currentStep !== 3 && currentStep !== 4 && currentStep !== 5 && currentStep !== 6 && <NavigationButtons onDisabledClick={handleDisabledButtonClick} />}
              {currentStep >= 4 && currentStep < 11 && currentStep !== 6 && currentStep !== 10 && (
                <div className="mt-10 text-center text-[#2B2B2Bcc] text-[14px] font-medium">
                  {tReset('wantToStartOver')} &nbsp;<button onClick={handleResetClick} className="cursor-pointer underline">{tReset('resetQuiz')}</button>
                </div>
              )}
              {currentStep === 5 && (
                <div className="max-w-[820px] w-full mx-auto px-6 md:px-14 mt-8">
                  <div className="text-Inter text-center max-w-[330px] mx-auto w-full text-[12px] text-[#555555]">
                    {tCommon('consent.byContinuing')} &nbsp;
                    <Link href="/terms" className="underline" target="_blank">{tCommon('consent.termsAndConditions')}</Link>
                    &nbsp; {tCommon('consent.and')} &nbsp;
                    <Link href="/privacy" className="underline" target="_blank">{tCommon('consent.privacyPolicy')}</Link>
                  </div>
                </div>
              )}

            </div>

            {currentStep === 11 && (
              <>
                <div className="relative max-w-[820px] w-full mx-auto px-6 md:px-14 mt-4 md:pb-36">
                  <TestimonialSlider testimonials={testimonialSliderJson} />
                  <div className="mt-10 -ml-5">
                    <BookTailorInfoSmall />
                  </div>
                </div>
              </>
            )}

            {/* Action Widget - Fixed position buttons above support widget */}
            <ActionWidget />
          </>
        )}
      </div >
    </>
  );
}

// Main export with Suspense boundary
export default function CreateBookProPage() {
  return (
    <Suspense fallback={<RestoringLoader />}>
      <CreateBookProContent />
    </Suspense>
  );
}